import { useEffect, useState } from 'react';
import {
  Button,
  Divider,
  Group,
  Input,
  InputWrapper,
  Modal,
  Select,
  Stack,
  Switch,
  Textarea,
} from '@mantine/core';
import { IMaskInput } from 'react-imask';
import type { MessagingView } from '@/types/messaging';
import styles from './EditMessagingModal.module.sass';
import { PHONE_NUMBER_MASK } from '@/constants';
import { formatPhoneNumber, isCompleteUsPhoneNumber, isPhoneValueEmpty, toServerPhoneNumber } from '@/helpers';
import { parseToE164 } from '@/shared/utils';
import { TimePicker } from '@/components/TimePicker';
import { BILLING_METHOD, BULLETIN_TYPE, type DAYS_OF_WEEK } from '@/types/enums';
import { useForm } from '@mantine/form';
import { DAYS_OF_WEEK_SELECT_OPTIONS } from '@/types/staticDictionaries';
import { SaveErrorBox } from '@/components/SaveErrorBox/SaveErrorBox';
import type { AxiosError } from 'axios';
import type { ErrorResponse } from '@/types/error';

type EditMessagingModalProps = {
  opened: boolean;
  messagingData: MessagingView | null;
  onClose: () => void;
  onSave: (data: MessagingView) => Promise<void> | void;
};

const INPUT_MAX_LENGTH = 0;
const MINUTES_STEP = 30;
const HOURS_STEP = 1;

const BILLING_METHOD_OPTIONS = [
  { value: BILLING_METHOD.CREDIT_CARD, label: 'Credit Card' },
  { value: BILLING_METHOD.INVOICE, label: 'Invoice' },
  { value: BILLING_METHOD.NOT_BILLED, label: 'Not Billed' },
];

type FormValues = {
  isActive: boolean;
  sms: {
    isActive: boolean;
    number: string;
    billingMethod?: BILLING_METHOD;
  };
  bulletin: {
    isSend: boolean;
    day?: DAYS_OF_WEEK;
    time: string;
    isBulletinSmsEnabled: boolean;
  };
  comment: string;
};

export const EditMessagingModal = ({ opened, messagingData, onClose, onSave }: EditMessagingModalProps) => {
  const form = useForm<FormValues>({
    initialValues: {
      isActive: false,
      sms: {
        isActive: false,
        number: '',
        billingMethod: undefined,
      },
      bulletin: {
        isSend: false,
        day: undefined,
        time: '00:00',
        isBulletinSmsEnabled: false,
      },
      comment: '',
    },
    validateInputOnChange: ['sms.number'],
    validate: {
      sms: {
        number: (value, values) => {
          if (!values.sms?.isActive) return null;
          if (isPhoneValueEmpty(value)) {
            return 'SMS number cannot be empty';
          }
          if (!isCompleteUsPhoneNumber(value)) {
            return 'SMS number must be a valid US phone number (11 digits including country code 1)';
          }
          return null;
        },
      },
      bulletin: {
        day: (value, values) => {
          if (values.bulletin.isSend && (!value || value.trim().length < 1)) {
            return 'Day is required';
          }
          return null;
        },
      },
    },
  });

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isBulletinDisabled, setIsBulletinDisabled] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<AxiosError<ErrorResponse> | null>(null);

  useEffect(() => {
    if (opened && messagingData) {
      form.setValues({
        isActive: messagingData.isActive,
        sms: {
          isActive: messagingData.sms?.isActive ?? false,
          number: (() => {
            const raw = messagingData.sms?.number || '';
            const server = toServerPhoneNumber(raw);
            return isPhoneValueEmpty(server) ? '' : server;
          })(),
          billingMethod: messagingData.sms?.billingMethod,
        },
        bulletin: {
          isSend: messagingData.bulletin?.isSend || false,
          day: messagingData.bulletin?.day,
          time: messagingData.bulletin?.time || '00:00',
          isBulletinSmsEnabled:
            (messagingData.sms?.isActive ?? false) && (messagingData.bulletin?.isBulletinSmsEnabled || false),
        },
        comment: messagingData.comment || '',
      });
    }

    setIsBulletinDisabled(
      messagingData?.bulletinType === BULLETIN_TYPE.OTHER ||
        messagingData?.bulletinType === BULLETIN_TYPE.LPI ||
        messagingData?.bulletinType === BULLETIN_TYPE.NONE
    );
  }, [opened, messagingData]);

  const handleSave = async () => {
    if (!messagingData) return;

    const validation = form.validate();
    if (validation.hasErrors) {
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    try {
      const smsNumberE164 = parseToE164(form.values.sms.number);
      const updatedData: MessagingView = {
        ...messagingData,
        isActive: form.values.isActive,
        sms: {
          isActive: form.values.sms.isActive,
          number: smsNumberE164 || undefined,
          billingMethod: form.values.sms.billingMethod,
          authorizeAccount: messagingData.sms?.authorizeAccount,
        },
        bulletin:
          isBulletinDisabled || !form.values.bulletin.isSend
            ? null
            : {
                isSend: form.values.bulletin.isSend,
                day: form.values.bulletin.day,
                time: form.values.bulletin.time,
                isBulletinSmsEnabled: form.values.sms.isActive && form.values.bulletin.isBulletinSmsEnabled,
              },
        comment: form.values.comment.trim() || undefined,
      };

      await onSave(updatedData);
    } catch (error: unknown) {
      console.error('Failed to save messaging data:', error);
      setSaveError(error as AxiosError<ErrorResponse> | null);
    } finally {
      setIsSaving(false);
    }
  };

  const canSave =
    !form.values.isActive || !form.values.sms.isActive || (form.values.sms.isActive && form.values.sms.number);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Messaging Information"
      classNames={{
        header: styles.modalHeader,
        title: styles.modalTitle,
        close: styles.closeButton,
      }}
      size="lg"
    >
      <form onSubmit={form.onSubmit(handleSave)}>
        <Stack gap="md" pt="md">
          <Stack gap="xs">
            <Switch
              label="Messaging Active"
              labelPosition="left"
              checked={form.values.isActive}
              onChange={(e) => form.setFieldValue('isActive', e.currentTarget.checked)}
            />
          </Stack>

          {form.values.isActive && (
            <InputWrapper label="SMS">
              <Stack gap="xs" mt="xs">
                <Switch
                  checked={form.values.sms.isActive}
                  onChange={(e) => {
                    const isSmsActive = e.currentTarget.checked;
                    form.setFieldValue('sms.isActive', isSmsActive);
                    if (!isSmsActive) {
                      form.setFieldValue('bulletin.isBulletinSmsEnabled', false);
                    }
                  }}
                  label="Send/Receive SMS"
                  labelPosition="left"
                />
                {form.values.sms.isActive && (
                  <Stack gap="xs">
                    <Input.Wrapper label="SMS Number" error={form.errors['sms.number']} required>
                      <Input
                        component={IMaskInput}
                        mask={PHONE_NUMBER_MASK}
                        placeholder={PHONE_NUMBER_MASK}
                        value={formatPhoneNumber(form.values.sms.number)}
                        unmask={false}
                        onAccept={(value: string) => {
                          const server = toServerPhoneNumber(value);
                          form.setFieldValue('sms.number', isPhoneValueEmpty(server) ? '' : server);
                        }}
                      />
                    </Input.Wrapper>
                    <Select
                      label="Billing Method"
                      placeholder="Select billing method"
                      data={BILLING_METHOD_OPTIONS}
                      value={form.values.sms.billingMethod}
                      onChange={(value) => form.setFieldValue('sms.billingMethod', value as BILLING_METHOD | undefined)}
                      clearable
                    />
                  </Stack>
                )}
              </Stack>
            </InputWrapper>
          )}
          <InputWrapper label="Bulletin">
            <Stack gap="xs" mt="xs">
              <Switch
                checked={form.values.bulletin.isSend}
                onChange={(e) => form.setFieldValue('bulletin.isSend', e.currentTarget.checked)}
                label="Send Weekly Bulletin"
                labelPosition="left"
                mt="xs"
                disabled={isBulletinDisabled}
              />
              {form.values.bulletin.isSend && (
                <Group gap="md" align="flex-start">
                  <Select
                    label="Bulletin Send Day"
                    size="md"
                    data={DAYS_OF_WEEK_SELECT_OPTIONS}
                    value={form.values.bulletin.day}
                    onChange={(value) => form.setFieldValue('bulletin.day', value as DAYS_OF_WEEK)}
                    placeholder="Select day"
                    required
                    error={form.errors['bulletin.day']}
                    flex="2"
                    disabled={isBulletinDisabled}
                  />
                  <TimePicker
                    size="md"
                    label="Bulletin Send Time"
                    value={form.values.bulletin.time}
                    onChange={(value) => form.setFieldValue('bulletin.time', value || '00:00')}
                    withDropdown
                    format="12h"
                    hoursStep={HOURS_STEP}
                    minutesStep={MINUTES_STEP}
                    hoursInputProps={{
                      maxLength: INPUT_MAX_LENGTH,
                    }}
                    minutesInputProps={{
                      maxLength: INPUT_MAX_LENGTH,
                    }}
                    amPmSelectProps={{
                      // @ts-ignore
                      maxLength: INPUT_MAX_LENGTH,
                    }}
                    clearable
                    flex="1"
                    disabled={isBulletinDisabled}
                  />
                </Group>
              )}
              {form.values.bulletin.isSend && (
                <Switch
                  checked={form.values.bulletin.isBulletinSmsEnabled}
                  onChange={(e) =>
                    form.setFieldValue('bulletin.isBulletinSmsEnabled', e.currentTarget.checked)
                  }
                  label="Send Bulletin via SMS text message"
                  labelPosition="left"
                  disabled={isBulletinDisabled || !form.values.sms.isActive}
                />
              )}
            </Stack>
          </InputWrapper>
          <Textarea
            label="Comment"
            placeholder="Enter comment"
            value={form.values.comment}
            onChange={(e) => form.setFieldValue('comment', e.currentTarget.value)}
            minRows={3}
          />
          <Divider my="xs" />
          <SaveErrorBox error={saveError} />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSave} loading={isSaving} color="primary">
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};
